import { useApolloClient, useQuery } from '@apollo/client/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { usePerson } from '@/account/usePerson';
import { LiveRecorder } from '@/components/LiveRecorder/LiveRecorder';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { allJobs, isInFlight, JOBS, jobTitle, V1Error } from '@/papermill/api';
import { demoSecret, forgetDemoKey } from '@/papermill/demo-key';

import styles from './LiveJobContainer.module.css';
import { useLiveSession, type WithSecret } from './useLiveSession';

const LOCALES = [
  { value: 'en-IE', label: 'English (Ireland)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'en-US', label: 'English (US)' },
];

/** Record live on the Demo key: the words as they are said, then the recording's job page. */
export function LiveJobContainer() {
  const client = useApolloClient();
  const navigate = useNavigate();
  const { person } = usePerson();
  const [locale, setLocale] = useState(LOCALES[0].value);
  const { data } = useQuery(JOBS, { fetchPolicy: 'cache-and-network' });
  const running = useMemo(() => allJobs(data).find(isInFlight) ?? null, [data]);
  const secret = useRef<Promise<string> | null>(null);

  const withSecret = useCallback<WithSecret>(
    async (task) => {
      if (!person) {
        throw new Error('Not signed in.');
      }
      secret.current ??= demoSecret(client, person.id);
      try {
        return await task(await secret.current);
      } catch (failure) {
        if (failure instanceof V1Error && failure.status === 401) {
          forgetDemoKey(person.id);
          secret.current = demoSecret(client, person.id, { refresh: true });
          return task(await secret.current);
        }
        secret.current = null;
        throw failure;
      }
    },
    [client, person],
  );

  useEffect(() => {
    if (person && !secret.current) {
      secret.current = demoSecret(client, person.id);
      secret.current.catch(() => {
        secret.current = null;
      });
    }
  }, [client, person]);

  const onEnded = useCallback(
    (jobId: string) => {
      void client.refetchQueries({ include: [JOBS] });
      navigate(`/playground/${jobId}`, { replace: true });
    },
    [client, navigate],
  );

  const session = useLiveSession(withSecret, onEnded);
  const live = session.phase !== 'idle' && session.phase !== 'failed';

  useEffect(() => {
    if (!live) {
      return;
    }
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [live]);

  return (
    <div data-fill-viewport className={styles.page}>
      {live ? null : (
        <PageHeader
          back={<Link to="/playground/new?kind=audio">← Audio</Link>}
          title="Live"
          lede="Talk, and watch every word land on screen as you say it."
        />
      )}
      <LiveRecorder
        phase={session.phase}
        words={session.words}
        level={session.level}
        elapsedMs={session.elapsedMs}
        lagMs={session.lagMs}
        secondsRemaining={session.secondsRemaining}
        notice={session.notice}
        error={session.error}
        blocked={
          running && !live
            ? `“${jobTitle(running)}” is still being processed. The playground takes one job at a time on your account.`
            : null
        }
        locale={locale}
        locales={LOCALES}
        onLocale={setLocale}
        onStart={() => void session.start(locale)}
        onFinish={session.finish}
        onRetry={() => void session.start(locale)}
      />
    </div>
  );
}
